import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { SelectSheet } from './SelectSheet';
import './SelectField.css';

/**
 * SelectField - Modern dropdown/select component for Wassla
 * Uses bottom sheet on mobile, supports RTL, search, and Wassla design tokens
 */
export const SelectField = ({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  title,
  searchable: searchableProp,
  disabled = false,
  error = null,
  rtl: rtlProp,
  getLabel,
  getIcon,
  multiSelect = false,
  className = '',
  'data-testid': testId = 'select-field'
}) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  
  // Auto-detect RTL from i18n if not explicitly provided
  const isRTL = rtlProp !== undefined ? rtlProp : i18n.language?.startsWith('ar');
  
  // Auto-enable search when options > 6
  const searchable = searchableProp !== undefined ? searchableProp : options.length > 6;
  
  // Default label getter
  const getLabelFn = useCallback((opt) => {
    if (getLabel) return getLabel(opt);
    if (isRTL && opt.label_ar) return opt.label_ar;
    return opt.label || opt.value || '';
  }, [getLabel, isRTL]);
  
  // Default icon getter
  const getIconFn = useCallback((opt) => {
    if (getIcon) return getIcon(opt);
    return opt.icon || null;
  }, [getIcon]);
  
  // Find selected option(s)
  const selected = useMemo(() => {
    if (multiSelect) {
      const vals = Array.isArray(value) ? value : [];
      return options.filter(opt => vals.includes(opt.value));
    }
    return options.find(opt => opt.value === value) || null;
  }, [value, options, multiSelect]);
  
  // Display text for trigger
  const displayText = useMemo(() => {
    if (multiSelect && Array.isArray(selected) && selected.length > 0) {
      return selected.map(s => getLabelFn(s)).join(', ');
    }
    if (!multiSelect && selected) {
      return getLabelFn(selected);
    }
    return null;
  }, [selected, multiSelect, getLabelFn]);
  
  const handleSelect = useCallback((selectedValue) => {
    if (multiSelect) {
      const current = Array.isArray(value) ? value : [];
      const newValue = current.includes(selectedValue)
        ? current.filter(v => v !== selectedValue)
        : [...current, selectedValue];
      onChange(newValue);
    } else {
      onChange(selectedValue);
      setOpen(false);
    }
  }, [multiSelect, value, onChange]);
  
  const handleOpen = useCallback(() => {
    if (!disabled) {
      setOpen(true);
    }
  }, [disabled]);
  
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);
  
  return (
    <div 
      className={`wassla-select-wrapper ${className}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid={testId}
    >
      {label && (
        <label className="wassla-select-label">
          {label}
        </label>
      )}
      
      <button
        type="button"
        className={`wassla-select-trigger ${
          open ? 'is-open' : ''
        } ${
          error ? 'has-error' : ''
        } ${
          disabled ? 'is-disabled' : ''
        }`}
        onClick={handleOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testId}-trigger`}
      >
        <div className="wassla-select-trigger-content">
          {!multiSelect && selected && getIconFn(selected) && (
            <span className="wassla-select-icon">
              {getIconFn(selected)}
            </span>
          )}
          
          <span className={`wassla-select-text ${!displayText ? 'is-placeholder' : ''}`}>
            {displayText || placeholder || t('select')}
          </span>
          
          <ChevronDown 
            className={`wassla-select-chevron ${open ? 'is-open' : ''}`}
            size={18}
          />
        </div>
      </button>
      
      {error && (
        <span className="wassla-select-error">
          {error}
        </span>
      )}
      
      <SelectSheet
        open={open}
        onClose={handleClose}
        title={title || placeholder || t('select')}
        options={options}
        value={value}
        onSelect={handleSelect}
        searchable={searchable}
        getLabel={getLabelFn}
        getIcon={getIconFn}
        rtl={isRTL}
        multiSelect={multiSelect}
        testId={testId}
      />
    </div>
  );
};

export default SelectField;
